import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Search, Tags, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../../auth/useAuth';
import { DataPanel, StatusPill } from '../../components/DataStates';
import { InlineSpinner } from '../../components/LoadingIndicators';
import { apiRequest, getApiErrorMessage } from '../../lib/api';
import { useApiCollection } from '../../lib/useApiCollection';
import type { ProductCategory } from '../../types/domain';

interface CategoryDraft {
  name: string;
  is_active: boolean;
}

const emptyDraft: CategoryDraft = { name: '', is_active: true };

export function ProductCategoriesPage() {
  const { accessToken, isPlatformAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<ProductCategory | 'new' | null>(null);
  const [draft, setDraft] = useState<CategoryDraft>(emptyDraft);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  const params = new URLSearchParams({ ordering: 'name', page: String(page), page_size: '20' });
  if (debouncedSearch) params.set('search', debouncedSearch);
  const categories = useApiCollection<ProductCategory>(
    'product-categories-screen',
    `/product-categories/?${params.toString()}`,
    accessToken,
  );
  const mutation = useMutation({
    mutationFn: () => apiRequest<ProductCategory>(
      editing === 'new' ? '/product-categories/' : `/product-categories/${editing?.id}/`,
      {
        ...(accessToken ? { accessToken } : {}),
        method: editing === 'new' ? 'POST' : 'PATCH',
        body: JSON.stringify(draft),
      },
    ),
    onSuccess: async () => {
      setEditing(null);
      setDraft(emptyDraft);
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-categories-screen'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'product-categories'] });
      await queryClient.invalidateQueries({ queryKey: ['collection', 'products'] });
    },
  });

  function beginCreate() {
    mutation.reset();
    setDraft(emptyDraft);
    setEditing('new');
  }

  function beginEdit(category: ProductCategory) {
    mutation.reset();
    setDraft({ name: category.name, is_active: category.is_active });
    setEditing(category);
  }

  function closeForm() {
    if (mutation.isPending) return;
    setEditing(null);
    setDraft(emptyDraft);
    mutation.reset();
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  const rows = categories.data?.results ?? [];

  return (
    <>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Catalogue</p>
          <h1>Product categories</h1>
          <p>Browse the global BFC taxonomy used consistently across every client catalogue.</p>
        </div>
        <span className="record-count"><Tags size={16} /> {categories.data?.count ?? 0} categories</span>
      </header>

      <div className="catalogue-list-toolbar">
        <label className="search-field"><Search aria-hidden="true" size={17} /><input aria-label="Search product categories" onChange={(event) => setSearch(event.target.value)} placeholder="Search category" type="search" value={search} /></label>
        {isPlatformAdmin ? <button className="button primary" onClick={beginCreate} type="button"><Plus size={17} /> Add category</button> : null}
      </div>

      <DataPanel loading={categories.isLoading} error={categories.isError} empty={rows.length === 0}>
        <section className="content-card data-card">
          <div className="data-table-wrap"><table className="data-table">
            <thead><tr><th>Category</th><th>Products</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{rows.map((category) => <tr key={category.id}>
              <td><strong>{category.name}</strong></td>
              <td>{category.product_count}</td>
              <td><StatusPill value={category.is_active ? 'active' : 'inactive'} /></td>
              <td><div className="table-actions">{isPlatformAdmin ? <button className="text-button" onClick={() => beginEdit(category)} type="button"><Pencil size={14} /> Edit</button> : null}</div></td>
            </tr>)}</tbody>
          </table></div>
          <div className="attendance-pagination"><span>Page {page} · {categories.data?.count ?? 0} records</span><div><button className="button secondary" disabled={!categories.data?.previous} onClick={() => setPage((value) => Math.max(1, value - 1))} type="button">Previous</button><button className="button secondary" disabled={!categories.data?.next} onClick={() => setPage((value) => value + 1)} type="button">Next</button></div></div>
        </section>
      </DataPanel>

      {editing ? (
        <div className="confirmation-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) closeForm(); }} role="presentation">
          <section aria-labelledby="product-category-form-title" aria-modal="true" className="confirmation-dialog catalogue-form-dialog" role="dialog">
            <button aria-label="Close category form" className="icon-button confirmation-close" disabled={mutation.isPending} onClick={closeForm} type="button"><X size={19} /></button>
            <div className="card-heading"><div><h2 id="product-category-form-title">{editing === 'new' ? 'Add product category' : `Edit ${editing.name}`}</h2><p>Categories are global and managed only by Platform Administrators.</p></div></div>
            <form className="management-form" onSubmit={submit}>
              <div className="form-grid">
                <label>Category name<input placeholder="e.g. Beverages" required value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></label>
                <label className="checkbox-field"><input checked={draft.is_active} type="checkbox" onChange={(event) => setDraft({ ...draft, is_active: event.target.checked })} /> Available for products</label>
              </div>
              {mutation.isError ? <p className="form-error" role="alert">{getApiErrorMessage(mutation.error)}</p> : null}
              <div className="form-actions"><button className="button secondary" disabled={mutation.isPending} onClick={closeForm} type="button">Cancel</button><button className="button primary" disabled={mutation.isPending} type="submit">{mutation.isPending ? <InlineSpinner /> : null}{editing === 'new' ? 'Add category' : 'Save category'}</button></div>
            </form>
          </section>
        </div>
      ) : null}
    </>
  );
}
